import Close16 from '@carbon/icons-react/es/close/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { useBoolean } from 'ahooks';
import { omit } from 'lodash';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { components as SelectComponents } from 'react-select';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Task } from 'api/tasks';
import { ReactComponent as SearchSvg } from 'assets/svgs/search.svg';
import { Button } from 'components/atoms/button';
import { Popover } from 'components/atoms/popover';
import { Select, StyledControl } from 'components/atoms/select';
import { ISingleValue } from 'components/atoms/select/types';
import { StatusPicker } from 'components/atoms/status-picker';
import { toastService } from 'components/molecules/toast';
import {
  getDocTasks,
  selectActiveDocumentInfo,
  updateDocTaskIdNameMap,
} from 'data/documents';
import { useSelectedStatusOption, useStatusField } from 'data/fields/hooks';
import { Task as ITask } from 'data/tasks';
import { selectTasksByProjectId } from 'data/tasks/selectors';
import { useProjectAsRBACResource } from 'hooks/rbac/useWithAccess';
import { useTranslate } from 'hooks/use-translation';
import { useAddExistingTasksMutation } from 'queries';
import { logger } from 'services';
import {
  AddButton,
  ButtonWrapper,
  Ellipsis,
  NoOptionsWrapper,
  NoSearchTitle,
  OptionWrapper,
  PickerHeader,
  RoundedCheckbox,
  SelectContainer,
  TaskNameWrapper,
  Empty,
  Spacer,
} from './picker-styles';

const DropdownIndicator = (props, isSelected, onClear) => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      {isSelected ? <Close16 onClick={onClear} /> : <Search16 />}
    </SelectComponents.DropdownIndicator>
  );
};

const Control = styled(StyledControl)`
  margin: var(--spacing-03) var(--spacing-05);
`;

const TaskOption = (props) => {
  const { label, value, isSelected, ...rest } = props;

  const statusField = useStatusField();
  const selectedStatusOption = useSelectedStatusOption(value.fields || []);

  if (value === null) {
    return <></>;
  }

  return (
    <SelectComponents.Option isSelected={isSelected} {...rest}>
      <OptionWrapper>
        <TaskNameWrapper>
          <StatusPicker
            field={statusField}
            dropdownHeader={<FormattedMessage id="task_status" />}
            type={value.type}
          >
            <StatusPicker.Trigger
              shape="round"
              iconSize="small"
              selectedStatus={selectedStatusOption}
              disabled
            />
          </StatusPicker>
          <Ellipsis>{value.taskName}</Ellipsis>
        </TaskNameWrapper>

        <RoundedCheckbox checked={isSelected} />
      </OptionWrapper>
    </SelectComponents.Option>
  );
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'none' }),
};

interface Props {
  projectId: number;
  resourceSectionId: number;
  documentSectionId: number;
  isPrivate?: boolean;
  isReadOnly?: boolean;
}

export const TaskPicker: FC<Props> = ({
  projectId,
  resourceSectionId,
  documentSectionId,
  isPrivate,
  isReadOnly,
}) => {
  const [visible, { toggle: toggleVisibility, setFalse: closePopover }] =
    useBoolean(false);

  const dispatch = useDispatch();
  const { documentId } = useSelector(selectActiveDocumentInfo);
  const translate = useTranslate();
  const [selectedTasks, setSelectedTasks] = useState<ISingleValue[]>([]);

  const { mutate, isLoading } = useAddExistingTasksMutation(projectId, {
    onSuccess: () => {
      const tasks = selectedTasks
        .map((taskOption) => taskOption?.value as unknown as ITask)
        .map(
          (task) =>
            omit(task as any, ['project', 'subTasks', 'parentTask']) as ITask,
        );

      if (documentId) {
        dispatch(getDocTasks({ projectId, documentId }));
        dispatch(
          updateDocTaskIdNameMap({
            documentId: documentId as number,
            projectId: projectId as number,
            resourceSectionId,
            resourceObjects: tasks,
          }),
        );
      }
    },
    onError: (error) => {
      toastService({
        title: translate('task_picker.error.title'),
        message: translate('task_picker.error.message'),
      }).error();

      logger.error('Failed to add the existing task in doc-section', { error });
    },
    onSettled: () => closePopover(),
  });

  const [inputValue, setInputValue] = useState('');

  const selectRef = useRef();

  const projectTasks = useSelector(
    selectTasksByProjectId(ProjectId(projectId)),
    shallowEqual,
  );

  const existingTasks = projectTasks.filter(
    (task) => task.source?.metaData?.resourceSectionId !== resourceSectionId,
  );

  const taskOptions = useMemo(() => {
    return existingTasks.map((task) => ({ label: task.taskName, value: task }));
  }, [existingTasks]);

  const handleChange = (value: ISingleValue[]) => {
    setSelectedTasks([...value]);
  };

  const onTaskAdd = async () => {
    const tasks = selectedTasks
      .map((taskOption) => taskOption?.value)
      .map(
        (task) =>
          omit(task as any, ['project', 'subTasks', 'parentTask']) as Task,
      );

    const meta = {
      resourceSectionId,
      documentSectionId,
      private: isPrivate || false,
    };

    mutate({ tasks, meta });
  };

  const clearValue = () => {
    setInputValue('');
  };

  const isOptionsSelected = !!selectedTasks.length;

  const clearOptions = () => {
    setSelectedTasks([]);
  };

  const noOptions = () => (
    <NoOptionsWrapper>
      {taskOptions.length ? (
        <Empty>
          <SearchSvg />
          <NoSearchTitle>
            <FormattedMessage id="task_picker.no_tasks.title" />
          </NoSearchTitle>
          <Spacer>
            <FormattedMessage id="task_picker.no_tasks.message" />
          </Spacer>
          <Button type="link" onClick={clearValue}>
            <FormattedMessage id="task_picker.clear_search" />
          </Button>
        </Empty>
      ) : (
        <FormattedMessage id="task_picker.empty.placeholder" />
      )}
    </NoOptionsWrapper>
  );

  return (
    <Popover
      placement="rightBottom"
      trigger={['click']}
      visible={isReadOnly ? false : visible}
      onVisibleChange={toggleVisibility}
      destroyTooltipOnHide
      content={
        <SelectContainer>
          <div>
            <PickerHeader>
              <FormattedMessage id="tasks" />
            </PickerHeader>
            <Select
              isMulti
              menuIsOpen
              autoFocus
              isClearable={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              inputValue={inputValue}
              onInputChange={setInputValue}
              components={{
                DropdownIndicator: (props) =>
                  DropdownIndicator(props, isOptionsSelected, clearOptions),
                IndicatorSeparator: () => null,
                Option: TaskOption,
                Control,
              }}
              ref={selectRef}
              styles={selectStyles}
              placeholder={<FormattedMessage id="placeholder.search" />}
              options={taskOptions}
              value={selectedTasks}
              onChange={handleChange}
              noOptionsMessage={noOptions}
            />
          </div>

          <ButtonWrapper>
            <Button onClick={closePopover}>
              <FormattedMessage id="cancel" />{' '}
            </Button>
            <Button loading={isLoading} onClick={onTaskAdd} type="primary">
              <FormattedMessage
                id={
                  (selectedTasks ?? []).length
                    ? 'task.added.count.multiple'
                    : 'task.added.count.single'
                }
                values={{ count: (selectedTasks ?? []).length }}
              />
            </Button>
          </ButtonWrapper>
        </SelectContainer>
      }
    >
      <AddButton
        type="link"
        // rule="DOCUMENT_TASK:CREATE"
        rule="TASK:CREATE"
        resourceData={useProjectAsRBACResource(projectId)}
      >
        <FormattedMessage id="task.add.existing" />
      </AddButton>
    </Popover>
  );
};
